import _endpoints from '../../../../_endpoints'
import { useDelete } from '../../../../_hooks/useDelete'
import { useFetch } from '../../../../_hooks/useFetch'
import { useContext } from 'react'
import { TeamsContext } from '../Context/TeamsProvider'
import { usePost } from '../../../../_hooks/usePost'

export interface TeamMemberRecord {
  id: number
  sid: string
  displayName: string
  email: string
  jobTitle: string
  role: string
  status: string
  profilePictureBase64Encoded: string
}

export interface TeamMembersResponse {
  items: TeamMemberRecord[]
  totalCount: number
}

export const useTeamService = () => {
  const [isGettingApps, getTeamApps] = useFetch()
  const [isRemovingApp, removeApp] = useDelete('invdesktop')
  const [isLinking, linkApps] = usePost()
  const [isFetchingTeams, getTeamsData] = useFetch()
  const [isFetchingTeamDetails, getTeamDetails] = useFetch()
  const [isFetchingTeamMembers, getTeamMembers] = useFetch()
  const [, addTeamMember] = usePost()
  const [isUpdatingMemberRole, updateMemberRole] = usePost()
  const [isRemovingMember, removeMemberById] = useDelete('invdesktop')
  const [isDeletingTeam, deleteTeamById] = useDelete('invdesktop')
  const [isSubmittingTeam, saveNewTeam] = usePost()
  const [isUpdatingTeam, updateTeam] = usePost()
  const [isFetchingRecentItems, fetchRecentItems] = useFetch()
  const [, fetchTeamDetails] = useFetch()
  const { setTeamApps } = useContext(TeamsContext)

  const getTeamApplications = async (teamId: string) => {
    const response = await getTeamApps(_endpoints.teams.getTeamApps(teamId))
    return response?.items || []
  }

  const removeApplication = async (teamId: string, applicationId: string) => {
    const response = await removeApp({
      url: `${_endpoints.teams.removeApp(teamId, applicationId)}`
    })
    const appList = await getTeamApplications(teamId)
    setTeamApps(appList || [])
    return response
  }

  const linkApplications = async (teamId: string, payload: []) => {
    const response = await linkApps({
      url: `${_endpoints.teams.addApp(teamId)}`,
      data: payload
    })
    const teamApps = await getTeamApplications(teamId)
    setTeamApps(teamApps || [])
    return response
  }

  const getAllTeams = async (offset: number, pageSize: number) => {
    const response = await getTeamsData(
      `${_endpoints.teams.getAllTeams}?skipCount=${offset}&maxResultCount=${pageSize}`
    )
    return response
  }

  const getTeamDetailsById = async (teamId: string, options?: any) => {
    return await getTeamDetails(_endpoints.teams.getTeamDetailsById(teamId), options)
  }

  const getTeamMembersById = async (
    teamId: string,
    skipCount?: number,
    maxCount?: number
  ): Promise<TeamMembersResponse> => {
    return (await getTeamMembers(
      _endpoints.teams.getTeamMembersById(teamId, skipCount, maxCount)
    )) as TeamMembersResponse
  }

  const addTeamMembers = async (teamId: string, payload: any) => {
    const endpoint = `${_endpoints.teams.addTeamMembers}`.replace(':teamId', teamId)
    const response = await addTeamMember({ url: endpoint, data: { members: payload } })
    return response?.results || []
  }

  const updateTeamMemberRole = async (teamId: string, memberId: string | number, role: string) => {
    return await updateMemberRole({
      url: _endpoints.teams.updateTeamMemberRole(teamId, memberId),
      data: { role },
      method: 'put'
    })
  }

  const removeTeamMember = async (teamId: string, memberId: string | number) => {
    return await removeMemberById({
      url: _endpoints.teams.removeTeamMember(teamId, memberId)
    })
  }

  const deleteTeam = async (teamId: string) => {
    return await deleteTeamById({
      url: `${_endpoints.teams.deleteTeam}/${teamId}`
    })
  }

  const addNewTeam = async (payload: any) => {
    return await saveNewTeam({
      url: _endpoints.teams.createTeam,
      data: payload,
      hasFiles: true
    })
  }

  const updateTeamDetails = async (teamId: string, payload: any) => {
    const response = await updateTeam({
      url: `${_endpoints.teams.updateTeam}/${teamId}`,
      data: payload,
      method: 'put',
      hasFiles: true
    })
    return response
  }

  const getRecentItems = async (teamId: string) => {
    try {
      const response = await fetchRecentItems(_endpoints.teams.getRecentItems(teamId))
      if (response?.items) {
        return response.items
      } else if (Array.isArray(response)) {
        return response
      }
      return []
    } catch (error) {
      console.error('Error fetching recent items:', error)
      throw error
    }
  }

  const getTeamsDetails = async (teamId: string) => {
    return await fetchTeamDetails(_endpoints.teams.getTeamById(teamId))
  }

  return {
    isGettingApps,
    getTeamApplications,
    isRemovingApp,
    removeApplication,
    isLinking,
    linkApplications,
    isFetchingTeams,
    getAllTeams,
    isFetchingTeamDetails,
    getTeamDetailsById,
    isFetchingTeamMembers,
    getTeamMembersById,
    addTeamMembers,
    isUpdatingMemberRole,
    updateTeamMemberRole,
    isRemovingMember,
    removeTeamMember,
    isDeletingTeam,
    deleteTeam,
    isSubmittingTeam,
    addNewTeam,
    isUpdatingTeam,
    updateTeamDetails,
    isFetchingRecentItems,
    getRecentItems,
    getTeamsDetails
  }
}
